import React from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import PropTypes from 'prop-types'
import { Select, Text } from 'components/base'
import providesIntegrationActivitySettingsUpsert from 'components/integrationActivitySettings/dataSync/providesIntegrationActivitySettingsUpsert'
import providesIntegrationActivitySettingsRemove from 'components/integrationActivitySettings/dataSync/providesIntegrationActivitySettingsRemove'
import providesIntegrationActivityTargets from 'components/Integrations/dataSync/providesIntegrationActivityTargets'

import { SendActivityToIntegrationSection } from './utils'
import {
  createSuccessToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'
import ToggleWithLabel from 'components/base/Toggle/LabelWithToggle'

const defaultState = {
  enabled: false,
  target: {
    value: '',
    label: '',
  },
}

class ActivitySettingFromIntegration extends React.Component {
  static propTypes = {
    channelId: PropTypes.string.isRequired,
    integration: PropTypes.object.isRequired,
    activityTargets: PropTypes.array.isRequired,
    onNeedsReinstall: PropTypes.func.isRequired,
    applicationName: PropTypes.string.isRequired,
    activityTargetsLoaded: PropTypes.bool.isRequired,
    integrationActivitySettingsUpsert: PropTypes.func.isRequired,
  }

  state = defaultState

  constructor(props) {
    super(props)

    this.state = {
      ...this.state,
      ...props.integration.activitySettingsForChannel,
    }
    this.handleTargetChange = this.handleTargetChange.bind(this)
  }

  async handleTargetChange(value) {
    const {
      channelId,
      integration,
      activityTargets,
      createErrorToasterMessage,
      createSuccessToasterMessage,
      integrationActivitySettingsUpsert,
      integrationActivitySettingsRemove,
    } = this.props

    // cloning
    const previousState = { ...this.state, target: { ...this.state.target } }
    const target = activityTargets.find(target => target.value === value)

    // The user selected empty value for some reason
    if (target.value === defaultState.target.value) {
      this.setState(defaultState, async () => {
        try {
          await integrationActivitySettingsRemove({
            channelId,
            integrationId: integration.id,
          })

          createSuccessToasterMessage({
            title: `We will now stop broadcasting your channel activity.`,
          })
        } catch (error) {
          this.setState(previousState, () => {
            createErrorToasterMessage({
              title:
                'An error has occured while setting up your channel activity link',
              detail: `We have reverted your changes back to their previous status.
Please try again or reach out to support if the problem persists`,
            })
          })
        }
      })
      return
    }

    this.setState({ target, enabled: true }, async () => {
      try {
        await integrationActivitySettingsUpsert({
          target,
          channelId,
          integrationId: integration.id,
          enabled: true,
        })

        createSuccessToasterMessage({
          title: `We’ll send your channel updates to
           ${this.state.target.label}`,
        })
      } catch (error) {
        this.setState(previousState, () => {
          createErrorToasterMessage({
            title:
              'An error has occured while setting up your channel activity link',
            detail: `We have reverted your changes back to their previous status.
Please try again or reach out to support if the problem persists`,
          })
        })
      }
    })
  }

  handleSendActivityToggle = event => {
    const { checked: enabled } = event.target
    const {
      channelId,
      integration,
      activityTargets,
      createErrorToasterMessage,
      createSuccessToasterMessage,
      integrationActivitySettingsUpsert,
    } = this.props
    const { target } = this.state
    let newTarget = target

    // Switching to enabled but no target is selected
    if (enabled && target.value === defaultState.target.value) {
      newTarget = activityTargets[1]
    }

    this.setState({ enabled, target: newTarget }, async () => {
      try {
        await integrationActivitySettingsUpsert({
          target,
          enabled,
          channelId,
          integrationId: integration.id,
        })
        if (!enabled) {
          return createSuccessToasterMessage({
            title: `We’ll stop sending your channel updates to ${
              this.state.target.label
            }`,
          })
        }

        createSuccessToasterMessage({
          title: `We’ll send your channel updates to
           ${this.state.target.label}`,
        })
      } catch (error) {
        this.setState({ enabled: !enabled, target }, () => {
          createErrorToasterMessage({
            title:
              'An error has occured while setting up your channel activity link',
            detail: `We have reverted your changes back to their previous status.
Please try again or reach out to support if the problem persists`,
          })
        })
      }
    })
  }

  render() {
    let { target } = this.state
    const { enabled: checked } = this.state
    const {
      activityTargets,
      activityTargetsLoaded,
      applicationName,
    } = this.props

    if (activityTargetsLoaded === false) {
      return (
        <SendActivityToIntegrationSection applicationName={applicationName}>
          <Text.Detail>Loading...</Text.Detail>
        </SendActivityToIntegrationSection>
      )
    }

    const hasActivityTargets = activityTargets.length > 1 // first value is placeholder
    const targetStillExists = activityTargets.find(
      _target => target.value === _target.value
    )

    if (!targetStillExists) {
      // ? how does this happen?
      // If people remove targets (slack channels for instance) they might not be retrieved anymore.
      // Not having that target absolutely breaks the Select below.
      // This might lead to weird situations where:
      // - User A sets activity on target 1
      // - User B tries to set it but doesn't have access to target 1 so we disable the toggle (the select still works though!)
      // We could improve this by adding the information of who has set this to help users understand the issue.
      // We'll do as next version
      target = defaultState.target
    }

    return (
      <ToggleWithLabel
        checked={checked}
        disabled={!hasActivityTargets}
        onToggle={this.handleSendActivityToggle}
        label="Send activity to Slack"
      >
        <Select
          className="margin-bottom-normal"
          value={target.value}
          buttonProps={{
            style: {
              width: 240,
            },
          }}
          bodyProps={{
            style: {
              width: 240,
              maxHeight: 300,
              overflow: 'auto',
            },
          }}
          onChange={this.handleTargetChange}
          options={activityTargets.map(({ label: title, value }) => ({
            title,
            value,
          }))}
        />
        <Text.SmallDetail>
          Receive updates in your slack channel when a doc is created, updated,
          or commented.
        </Text.SmallDetail>
      </ToggleWithLabel>
    )
  }
}

export default compose(
  connect(
    null,
    {
      createSuccessToasterMessage,
      createErrorToasterMessage,
    }
  ),
  providesIntegrationActivityTargets(),
  providesIntegrationActivitySettingsUpsert,
  providesIntegrationActivitySettingsRemove
)(ActivitySettingFromIntegration)
